import { useReducer, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { _ } from 'lodash';
import { contextReducerLogging } from 'config';
// import directly from interface to avoid circular dependency
import { Organizations, OrganizationalMemberships } from 'actions/sharedJsActions';

import { Hooks } from '../../shared/interface/sharedJsInterface';

// Hooks
const { B2b } = Hooks;
const { useLoadB2BStaffOrganizationsIfNeeded } = B2b;

// Actions
const { organizationUnloadMembers, organizationMembersFetch } = Organizations;
const { bulkUploadUsers, updateMemberships, fetchBulkUserCreationSnapshot } = OrganizationalMemberships;

export const initialState = () => ({
  deactivedMemberships: [],
  selectedMemberships: [],
  usersData: [],
  loading: true,
  unload: false,
  selectedRole: "all",
  selectedUserType: "",
  selectedUploadRole: "",
  orgUserTypes: [],
  bulkUpload: false,
  page: 1,
  searchQuery: "",
  sortKey: "",
  organization: null,
  deactivateUser: false,
  snapshotId: null,
  snapshotData: null,
});

export const requestFormatter = ({
  deactivedMemberships,
  selectedMemberships,
  usersData,
  loading,
  unload,
  selectedRole,
  selectedUserType,
  selectedUploadRole,
  orgUserTypes,
  bulkUpload,
  page,
  searchQuery,
  sortKey,
  organization,
  deactivateUser,
}) =>
  ({
    deactived_memberships: deactivedMemberships,
    selected_memberships: selectedMemberships,
    users_data: usersData,
    loading,
    unload,
    selected_role: selectedRole,
    selected_user_type: selectedUserType,
    selected_upload_role: selectedUploadRole,
    org_user_types: orgUserTypes,
    bulk_upload: bulkUpload,
    page,
    search_query: searchQuery,
    sort_key: sortKey,
    organization: organization,
    deactivate_user: deactivateUser
  });

const reducer = (state, action) => {
  const { type, payload } = action;
  if (contextReducerLogging) {
    console.log('%c%s',
      'color: green; background: yellow;', type, payload);
  }
  switch (type) {
    case "SET_USERS":
      return { ...state, usersData: payload.usersData };
    case "SET_PAGE":
      return { ...state, page: payload.page };
    case "UNLOAD_MEMBERSHIP":
      return { ...state, selectedMemberships: [] };
    case "SET_SEARCH_QUERY":
      return { ...state, searchQuery: payload.searchQuery };
    case "SET_ROLE":
      return { ...state, selectedRole: payload.selectedRole };
    case "SET_USER_TYPE":
      return { ...state, selectedUserType: payload.selectedUserType };
    case "SET_UPLOAD_ROLE":
      return { ...state, selectedUploadRole: payload.selectedUploadRole };
    case "SET_ORG_USER_TYPES":
      return { ...state, orgUserTypes: payload.orgUserTypes };
    case "SET_UNLOAD":
      return { ...state, unload: payload.unload };
    case "SET_LOADING":
      return { ...state, loading: payload.loading };
    case "SET_SORT_KEY":
      return { ...state, sortKey: payload.sortKey, page: 1 };
    case "SET_DEACTIVED_MEMBERSHIPS":
      return { ...state, deactivedMemberships: payload.deactivedMemberships };
    case "SET_MEMBERSHIPS":
      return { ...state, selectedMemberships: payload.selectedMemberships };
    case "SET_ORGANIZATION":
      return { ...state, organization: payload.organization };
    case "DEACTIVATE_USERS":
      return { ...state, deactivateUser: payload.deactivateUser };
    case "BULK_UPLOAD_USERS":
      return { ...state, bulkUpload: payload.bulkUpload };
    case "SET_SNAPSHOT_ID":
      return { ...state, snapshotId: payload.snapshotId };
    case "FETCH_BULK_USER_CREATION_SNAPSHOT_SUCCESS":
      return { ...state, snapshotData: payload.snapshotData };
    default:
      return state;
  }
};

export function useUserManagementReducer() {
  const [state, dispatch] = useReducer(reducer, initialState());
  const session = useSelector(store => store.session);
  const selectedOrganizationId = session.get("selected_organization_id");
  const loadingStaffOrg = useLoadB2BStaffOrganizationsIfNeeded();
  const org = useSelector(store => store.organizations.get(selectedOrganizationId));
  const reduxDispatch = useDispatch();
  const {
    unload,
    bulkUpload,
    usersData,
    selectedUserType,
    selectedUploadRole,
    orgUserTypes,
    page,
    searchQuery,
    sortKey,
    deactivateUser,
    selectedMemberships,
    deactivedMemberships,
    snapshotId,
    snapshotData,
  } = state;

  useEffect(() => {
    dispatch({ type: "SET_UNLOAD", payload: { unload: true } });
  }, [selectedOrganizationId]);

  useEffect(() => {
    if (loadingStaffOrg === true) return;
    dispatch({ type: "SET_ORGANIZATION", payload: { organization: org } });
  }, [loadingStaffOrg]);

  useEffect(() => {
    if (orgUserTypes.length > 0) return;
    dispatch({ type: "SET_ORG_USER_TYPES", payload: { orgUserTypes: org.get("user_types") } });
  }, [org]);

  useEffect(() => {
    if (unload === false) return;
    reduxDispatch(organizationUnloadMembers());
    dispatch({ type: "SET_UNLOAD", payload: { unload: false } });
    dispatch({ type: "UNLOAD_MEMBERSHIP", payload: { unload: false } });
    dispatch({ type: "SET_PAGE", payload: { page: 1 } });
  }, [unload]);

  useEffect(() => {
    if (unload === true) return;
    reduxDispatch(organizationMembersFetch({
      organizationId: selectedOrganizationId,
      loadSubscriptions: false,
      query: searchQuery,
      pageNumber: page,
      sortKey: sortKey,
      fullAssociations: true
    }));
  }, [page, searchQuery, sortKey]);

  useEffect(() => {
    if (!bulkUpload) return;
    const transformedUsers = usersData.map(userData => ({
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
    }));
    reduxDispatch(bulkUploadUsers(selectedOrganizationId, transformedUsers, selectedUploadRole, selectedUserType))
      .then(response => response).catch((error) => { alert(error); });
    dispatch({ type: "BULK_UPLOAD_USERS", payload: { bulkUpload: false } });
    dispatch({ type: "SET_USERS", payload: { usersData: [] } });
  }, [bulkUpload]);

  useEffect(() => {
    if (!deactivateUser) return;
    reduxDispatch(updateMemberships(selectedOrganizationId, selectedMemberships, true))
      .then(response => response).catch((error) => { alert(error); });
    const deactivated = [...deactivedMemberships, ...selectedMemberships];
    dispatch({ type: "SET_DEACTIVED_MEMBERSHIPS", payload: { deactivedMemberships: deactivated } });
    dispatch({ type: "SET_MEMBERSHIPS", payload: { selectedMemberships: [] } });
    dispatch({ type: "DEACTIVATE_USERS", payload: { deactivateUser: false } });
  }, [deactivateUser]);

  useEffect(() => {
    if (!snapshotId) return;
    reduxDispatch(fetchBulkUserCreationSnapshot(snapshotId))
      .then((response) => {
        dispatch({ type: 'FETCH_BULK_USER_CREATION_SNAPSHOT_SUCCESS', payload: { snapshotData: response } });
        return response;
      })
      .catch((error) => { alert(error); });
  }, [snapshotId]);

  return {
    state,
    dispatch
  };
}
